import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

export default function pinnedList() {

    const blocks = document.querySelectorAll('[data-pinned-list]');
    if (!blocks.length) return;

    blocks.forEach(block => {

        const section = block.closest('section.block');
        if (!section) return;

        const tabs = Array.from(block.querySelectorAll('.pinned-list__tab'));
        const lists = Array.from(block.querySelectorAll('.pinned-list__list'));
        const numSections = tabs.length;

        if (numSections < 1) return;

        let currentIndex = 0;
        let st = null;

        // ----------------------------------------
        // Estado inicial: primera lista visible
        // ----------------------------------------
        lists.forEach((list, i) => {
            gsap.set(list, { autoAlpha: i === 0 ? 1 : 0, y: 0 });
            list.classList.toggle('is-active', i === 0);
        });

        // ----------------------------------------
        // Mostrar sección por índice (con transición)
        // ----------------------------------------
        const goToSection = (index) => {
            if (index === currentIndex) return;

            const prev = lists[currentIndex];
            const next = lists[index];

            gsap.timeline()
                .to(prev, { autoAlpha: 0, y: -16, duration: 0.25, ease: 'power2.in' })
                .call(() => {
                    prev.classList.remove('is-active');
                    next.classList.add('is-active');
                    tabs[currentIndex].classList.remove('is-active');
                    tabs[currentIndex].setAttribute('aria-selected', 'false');
                    tabs[index].classList.add('is-active');
                    tabs[index].setAttribute('aria-selected', 'true');
                    currentIndex = index;
                })
                .fromTo(
                    next,
                    { autoAlpha: 0, y: 16 },
                    { autoAlpha: 1, y: 0, duration: 0.3, ease: 'power2.out' }
                );
        };

        // ----------------------------------------
        // Resetear al primer tab sin animación
        // ----------------------------------------
        const reset = () => {
            tabs.forEach((tab, i) => {
                tab.classList.toggle('is-active', i === 0);
                tab.setAttribute('aria-selected', i === 0 ? 'true' : 'false');
            });
            lists.forEach((list, i) => {
                list.classList.toggle('is-active', i === 0);
                gsap.set(list, { autoAlpha: i === 0 ? 1 : 0, y: 0 });
            });
            currentIndex = 0;
        };

        // ----------------------------------------
        // ScrollTrigger: pin + avance de secciones
        // Cada sección ocupa 100vh de scroll
        // ----------------------------------------
        const createScrollTrigger = () => {
            const scrollHeight = Math.max(numSections - 1, 1) * window.innerHeight;

            st = ScrollTrigger.create({
                trigger: section,
                start: 'top top',
                end: `+=${scrollHeight}`,
                pin: true,
                pinSpacing: true,
                invalidateOnRefresh: true,

                onUpdate(self) {
                    const newIndex = Math.min(
                        Math.floor(self.progress * numSections),
                        numSections - 1
                    );
                    if (newIndex !== currentIndex) {
                        goToSection(newIndex);
                    }
                },
            });
        };

        // Sólo activar en desktop (>= 1024 px)
        const mm = gsap.matchMedia();

        mm.add('(min-width: 1024px)', () => {
            createScrollTrigger();

            return () => {
                // Cleanup al salir del breakpoint
                if (st) {
                    st.kill();
                    st = null;
                }
                reset();
            };
        });

        // ----------------------------------------
        // Acordeón móvil (< 1024 px)
        // ----------------------------------------
        mm.add('(max-width: 1023px)', () => {
            const accItems = Array.from(block.querySelectorAll('.pinned-list__acc-item'));
            const accTriggers = Array.from(block.querySelectorAll('.pinned-list__acc-trigger'));
            const accPanels = Array.from(block.querySelectorAll('.pinned-list__acc-panel'));

            // Estado inicial: primer panel abierto, resto cerrado
            // accPanels.forEach((panel, i) => {
            //     gsap.set(panel, { height: i === 0 ? 'auto' : 0 });
            // });

            const openItem = (index) => {
                accItems[index].classList.add('is-open');
                accTriggers[index].setAttribute('aria-expanded', 'true');
                accPanels[index].setAttribute('aria-hidden', 'false');
                gsap.to(accPanels[index], { height: 'auto', duration: 0.3, ease: 'power2.inOut' });
            };

            const closeItem = (index) => {
                accItems[index].classList.remove('is-open');
                accTriggers[index].setAttribute('aria-expanded', 'false');
                accPanels[index].setAttribute('aria-hidden', 'true');
                gsap.to(accPanels[index], { height: 0, duration: 0.3, ease: 'power2.inOut' });
            };

            const handlers = accTriggers.map((trigger, i) => {
                const handler = () => {
                    if (accItems[i].classList.contains('is-open')) {
                        closeItem(i);
                    } else {
                        accItems.forEach((_, j) => {
                            if (j !== i && accItems[j].classList.contains('is-open')) closeItem(j);
                        });
                        openItem(i);
                    }
                };
                trigger.addEventListener('click', handler);
                return handler;
            });

            return () => {
                // Cleanup: eliminar listeners y resetear alturas
                accTriggers.forEach((trigger, i) => trigger.removeEventListener('click', handlers[i]));
                accPanels.forEach(panel => gsap.set(panel, { clearProps: 'height' }));
            };
        });

        // ----------------------------------------
        // Click manual en tabs (accesibilidad / mobile)
        // ----------------------------------------
        tabs.forEach((tab, i) => {
            tab.addEventListener('click', () => goToSection(i));
        });

    });

}
